import axios from 'axios';
import { getStoredToken, setStoredToken, clearStoredToken } from '../utils/tokenStorage';
import { expiredSessionLoginPath } from '../utils/authReturnPath';
import { stripLocalePrefix } from '../utils/localePaths';

export const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  (process.env.REACT_APP_BUILD_TARGET === 'toss' ? 'https://mobidicsaju.com/api' : '/api')
).replace(/\/+$/, '');

export const apiUrl = (path = '') => {
  if (!path) return API_BASE_URL;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// 토큰 갱신 중복 방지
let isRefreshing = false;
let refreshSubscribers = [];

function onTokenRefreshed(newToken) {
  refreshSubscribers.forEach(({ resolve }) => resolve(newToken));
  refreshSubscribers = [];
}

function rejectRefreshSubscribers(error) {
  refreshSubscribers.forEach(({ reject }) => reject(error));
  refreshSubscribers = [];
}

function redirectToLogin() {
  if (stripLocalePrefix(window.location.pathname) !== '/login') {
    window.location.href = expiredSessionLoginPath(window.location);
  }
}

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const vid = localStorage.getItem('visitor_id');
  if (vid) config.headers['X-Visitor-Id'] = vid;
  return config;
}, (error) => Promise.reject(error));

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (!originalRequest) return Promise.reject(error);

    // 401 에러이고 refresh 요청 자체가 아닌 경우에만 토큰 갱신 시도
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== '/auth/refresh' &&
      originalRequest.url !== '/auth/login'
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // 이미 갱신 중이면 갱신 완료 후 재시도
        return new Promise((resolve, reject) => {
          refreshSubscribers.push({ resolve: (newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          }, reject });
        });
      }

      isRefreshing = true;

      try {
        const token = getStoredToken();
        if (!token) throw new Error('No token');

        const res = await axios.post(apiUrl('/auth/refresh'), {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });

        if (res.data.success && res.data.token) {
          const newToken = res.data.token;
          setStoredToken(newToken);
          onTokenRefreshed(newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
        throw new Error('Refresh failed');
      } catch {
        // 토큰 갱신 실패 시 로그아웃
        clearStoredToken();
        rejectRefreshSubscribers(error);
        redirectToLogin();
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    // refresh 자체가 실패하거나 login 401인 경우
    if (error.response?.status === 401 && originalRequest.url === '/auth/refresh') {
      clearStoredToken();
      redirectToLogin();
    }

    return Promise.reject(error);
  }
);

// ═══ 사주+농사 융합 API ═══
const FARM_TIMEOUT = { timeout: 120000 }; // Gemini AI 호출 포함 → 120초
export const sajuFarmAPI = {
  getOhengPlant: (payload) => api.post('/saju-farm/oheng-plant', payload, FARM_TIMEOUT),
  getSajuYakson: (payload) => api.post('/saju-farm/saju-yakson', payload, FARM_TIMEOUT),
  getJeolgiFortune: (payload) => api.post('/saju-farm/jeolgi-fortune', payload, FARM_TIMEOUT),
  getHealingFarm: (payload) => api.post('/saju-farm/healing-farm', payload, FARM_TIMEOUT),
  getComprehensive: (payload) => api.post('/saju-farm/comprehensive', payload, FARM_TIMEOUT),
  getPungsuWeather: (payload) => api.post('/saju-farm/pungsu-weather', payload, FARM_TIMEOUT),
  getDataSources: () => api.get('/saju-farm/data-sources'),
  searchAnnals: (payload) => api.post('/saju-farm/annals-search', payload, FARM_TIMEOUT),
  searchGeography: (payload) => api.post('/saju-farm/geography-search', payload, FARM_TIMEOUT),
};

export const reviewsAPI = {
  getLive: (params = {}) => api.get('/reviews/live', { params }).then((res) => res.data),
  createLive: (payload) => api.post('/reviews/live', payload).then((res) => res.data),
};

// ═══ 유명인 사주 API (상세 첫 조회 시 AI 분석 자동생성 → 120초) ═══
export const celebrityAPI = {
  list: (params = {}) => api.get('/celebrities', { params }).then((res) => res.data),
  get: (slug) => api.get(`/celebrities/${encodeURIComponent(slug)}`, { timeout: 120000 }).then((res) => res.data),
};

// ═══ 점집 지도 디렉토리 API (공개 목록/검색 + 관리자 CRUD) ═══
export const jeomjipAPI = {
  list: (params = {}) => api.get('/jeomjip', { params }).then((res) => res.data),
  listAll: () => api.get('/jeomjip/all').then((res) => res.data),
  create: (payload) => api.post('/jeomjip', payload).then((res) => res.data),
  update: (id, payload) => api.put(`/jeomjip/${id}`, payload).then((res) => res.data),
  remove: (id) => api.delete(`/jeomjip/${id}`).then((res) => res.data),
  geocode: (address) => api.post('/jeomjip/geocode', { address }).then((res) => res.data),
};

// ═══ 서양 점성술 출생천궁도 API (계산 + AI 해석 → 120초) ═══
export const astrologyAPI = {
  natalChart: (payload) => api.post('/astrology/natal-chart', payload, { timeout: 300000 }),
  getShared: (shareId) => api.get(`/astrology/reading/${encodeURIComponent(shareId)}`).then((res) => res.data),
};

// ═══ 라이브 라운지 API (단체채팅 / 라이브 콜 / 라디오 — lunamoon 소셜 포팅) ═══
export const liveAPI = {
  // 단체 채팅
  chatRooms: (params = {}) => api.get('/live/chat', { params }).then((r) => r.data),
  createChatRoom: (payload) => api.post('/live/chat', payload).then((r) => r.data),
  chatRoom: (id) => api.get(`/live/chat/${id}`).then((r) => r.data),
  chatMessages: (id, params = {}) => api.get(`/live/chat/${id}/messages`, { params }).then((r) => r.data),
  sendChatMessage: (id, payload) => api.post(`/live/chat/${id}/messages`, payload).then((r) => r.data),
  markChatRead: (id, lastMessageId) => api.post(`/live/chat/${id}/read`, { last_message_id: lastMessageId }).then((r) => r.data),
  leaveChatRoom: (id, payload = {}) => api.post(`/live/chat/${id}/leave`, payload).then((r) => r.data),
  addAnnouncement: (id, content) => api.post(`/live/chat/${id}/announcements`, { content }).then((r) => r.data),
  deleteAnnouncement: (id, aid) => api.delete(`/live/chat/${id}/announcements/${aid}`).then((r) => r.data),
  kickMember: (id, uid) => api.post(`/live/chat/${id}/members/${uid}/kick`).then((r) => r.data),
  unkickMember: (id, uid) => api.post(`/live/chat/${id}/members/${uid}/unkick`).then((r) => r.data),
  banMember: (id, uid, durationMinutes = 0) => api.post(`/live/chat/${id}/members/${uid}/ban`, { duration_minutes: durationMinutes }).then((r) => r.data),
  unbanMember: (id, uid) => api.post(`/live/chat/${id}/members/${uid}/unban`).then((r) => r.data),
  deleteChatRoom: (id, payload = {}) => api.delete(`/live/chat/${id}`, { data: payload }).then((r) => r.data),
  uploadChatImage: (file) => {
    const form = new FormData();
    form.append('image', file);
    return api.post('/live/chat/upload-image', form, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000,
    }).then((r) => r.data);
  },
  // 라이브 콜
  voiceRooms: () => api.get('/live/voice').then((r) => r.data),
  createVoiceRoom: (payload) => api.post('/live/voice', payload).then((r) => r.data),
  joinVoiceRoom: (id, mode = 'speaker') => api.post(`/live/voice/${id}/join`, { mode }).then((r) => r.data),
  joinChatVoice: (chatRoomId) => api.post(`/live/voice/chat/${chatRoomId}/join`).then((r) => r.data),
  voiceMessages: (id, params = {}) => api.get(`/live/voice/${id}/messages`, { params }).then((r) => r.data),
  sendVoiceMessage: (id, content) => api.post(`/live/voice/${id}/messages`, { content }).then((r) => r.data),
  leaveVoiceRoom: (id) => api.post(`/live/voice/${id}/leave`).then((r) => r.data),
  raiseHand: (id, raised) => api.post(`/live/voice/${id}/hand`, { raised }).then((r) => r.data),
  changeVoiceRole: (id, userId, role) => api.post(`/live/voice/${id}/role`, { user_id: userId, role }).then((r) => r.data),
  closeVoiceRoom: (id) => api.delete(`/live/voice/${id}`).then((r) => r.data),
  // 라디오
  radioLive: () => api.get('/live/radio/live').then((r) => r.data),
  radioStart: (payload) => api.post('/live/radio/start', payload).then((r) => r.data),
  radioEnd: (payload = {}) => api.post('/live/radio/end', payload).then((r) => r.data),
  radioMy: () => api.get('/live/radio/my').then((r) => r.data),
  radioDetail: (id, params = {}) => api.get(`/live/radio/${id}`, { params }).then((r) => r.data),
  radioByRoom: (roomName) => api.get(`/live/radio/by-room/${encodeURIComponent(roomName)}`).then((r) => r.data),
  radioLeave: (id, sessionId) => api.post(`/live/radio/${id}/leave`, { session_id: sessionId }).then((r) => r.data),
  radioComments: (id, afterId = 0) => api.get(`/live/radio/${id}/comments`, { params: { after_id: afterId } }).then((r) => r.data),
  radioComment: (id, content) => api.post(`/live/radio/${id}/comments`, { content }).then((r) => r.data),
  radioAnnouncement: (id, announcement) => api.put(`/live/radio/${id}/announcement`, { announcement }).then((r) => r.data),
  radioInvite: (id, userId) => api.post(`/live/radio/${id}/invite`, { user_id: userId }).then((r) => r.data),
  radioAcceptInvite: (id) => api.post(`/live/radio/${id}/accept-invite`).then((r) => r.data),
};

export default api;
